import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ModalCustom from 'components/Modal/Modal';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { closeDialogAction, openDialogAction } from 'store/actions/dialogsActions';
import { useAppState } from 'store/Provider';
import { setActiveOrderIdAction } from '../../../store/actions/managementActions';
import useManagementService from '../services/useManagementService';

const ManagementCloseDialog = () => {
  const { state, dispatch } = useAppState();
  const { dialogs, management, loadings } = state;
  const isOpen = dialogs.managementCloseDialog;
  const isCancelReasonOpen = dialogs.cancelReasonDialog;
  const [isConfirm, setIsConfirm] = useState<boolean | null>(null);
  const [reason, setReason] = useState<string>('');
  const { closeOrder } = useManagementService();
  const [t] = useTranslation();
  const confirmText = t('OK');
  const cancelText = t('Cancel');
  const loading = () => !!loadings[`close_${management.activeOrderId}`] || !!loadings.orders;
  const onConfirm = async () => {
    const activeOrderData = management?.orders?.find((order) => order.id === management.activeOrderId);
    if (!activeOrderData) return;
    if (
      activeOrderData.nb_order_workunits > 0 &&
      activeOrderData.nb_submitted_deliverables === 0 &&
      activeOrderData.nb_accepted_deliverables === 0
    ) {
      // Nothing submitted — ask for reason
      onClose();
      dispatch(openDialogAction({ name: 'cancelReasonDialog' }));
      setReason('');
    } else {
      // Has submitted/accepted deliverables — close directly
      const response = await closeOrder(management.activeOrderId, '');
      if (response?.error) {
        setIsConfirm(null);
        onClose();
        onCloseReason();
      } else {
        setIsConfirm(true);
      }
    }
  };

  const onClose = () => {
    dispatch(closeDialogAction('managementCloseDialog'));
  };

  const onCloseReason = () => {
    dispatch(closeDialogAction('managementCloseDialog'));
    dispatch(closeDialogAction('cancelReasonDialog'));
    dispatch(setActiveOrderIdAction(null));
  };

  useEffect(() => {
    if (isOpen) {
      setIsConfirm(null);
      setReason('');
    }
  }, [isOpen]);

  const onCancelReasonConfirm = async () => {
    if (!reason.trim()) {
      alert(t('Reason_Is_Mandatory'));
      return;
    }
    const response = await closeOrder(management.activeOrderId, reason);
    if (response?.error) {
      setIsConfirm(null);
      onCloseReason();
    } else {
      setIsConfirm(true);
    }
  };

  const bodyIsConfimed = (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
      <CheckCircleIcon color="success" fontSize="large" sx={{ mb: 1 }} />
      <Typography color="textPrimary"> {t('Order_Closed_Message', { orderId: management.activeOrderId })}</Typography>
      <Typography variant="caption" color="textPrimary" sx={{ fontStyle: 'italic' }}>
        {t('Reopen_From_Order_Close_Page')}
      </Typography>
    </Box>
  );

  const bodyIsNotConfimed = (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
      <ErrorIcon color="error" fontSize="large" sx={{ mb: 1 }} />
      <Typography color="error">{t('Error_Occurred')}</Typography>
    </Box>
  );

  const bodyDefault = (
    <Box>
      {/* <Typography color="textPrimary">{t('Do you want to close this order ?')}</Typography> */}
      <Typography color="textPrimary">
        {t('Close_Order_Confirmation_Text', { orderId: management.activeOrderId })}
      </Typography>
    </Box>
  );

  const bodyCancelReasonDefault = (
    <Box>
      <Typography color="textPrimary" sx={{ mb: 2 }}>
        {t('Please_Provide_Reason_Cancellation')}
      </Typography>
      <TextField
        fullWidth
        multiline
        rows={4}
        placeholder={t('Enter_Reason_Here')}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        autoFocus
      />
    </Box>
  );

  const bodyContent = () => {
    switch (isConfirm) {
      case true:
        return bodyIsConfimed;
      case false:
        return bodyIsNotConfimed;
      default:
        return bodyDefault;
    }
  };

  const bodyCancelReasonContent = () => {
    switch (isConfirm) {
      case true:
        return bodyIsConfimed;
      case false:
        return bodyIsNotConfimed;
      default:
        return bodyCancelReasonDefault;
    }
  };

  const body = (
    <DialogContent>
      <DialogContentText component="div">{bodyContent()}</DialogContentText>
    </DialogContent>
  );

  const bodyCancelReason = (
    <DialogContent>
      <DialogContentText component="div">{bodyCancelReasonContent()}</DialogContentText>
    </DialogContent>
  );

  const actionsIsConfimed = (
    <Stack direction="row" sx={{ flex: 'auto', justifyContent: 'center' }}>
      <Button variant="contained" onClick={onCloseReason}>
        {t('Understood')}
      </Button>
    </Stack>
  );

  const actionsDefault = (
    <Stack direction="row" spacing={2}>
      <Button variant="outlined" onClick={onClose} disabled={loading()}>
        {cancelText}
      </Button>

      <Button variant="contained" onClick={onConfirm} loading={loading()}>
        {confirmText}
      </Button>
    </Stack>
  );

  const actionsReasonDefault = (
    <Stack direction="row" spacing={2}>
      <Button variant="outlined" onClick={onCloseReason} disabled={loading()}>
        {cancelText}
      </Button>
      <Button
        variant="contained"
        onClick={(e) => onCancelReasonConfirm()}
        loading={loading()}
        disabled={reason.trim() === ''}
      >
        {confirmText}
      </Button>
    </Stack>
  );

  const actionsContent = () => {
    switch (isConfirm) {
      case true:
        return actionsIsConfimed;
      case false:
        return null;
      default:
        return actionsDefault;
    }
  };

  const actionsReasonContent = () => {
    switch (isConfirm) {
      case true:
        return actionsIsConfimed;
      case false:
        return null;
      default:
        return actionsReasonDefault;
    }
  };

  const actions = <DialogActions>{actionsContent()}</DialogActions>;

  const actionsCancelReason = <DialogActions>{actionsReasonContent()}</DialogActions>;

  return (
    <>
      <ModalCustom
        open={isOpen}
        modalName="managementCloseDialog"
        onClose={onClose}
        onConfirm={onConfirm}
        disabledConfirm={isConfirm !== null}
        titleText={t('Close_Order')}
        body={body}
        actions={actions}
        size="md"
      />
      <ModalCustom
        open={isCancelReasonOpen}
        modalName="cancelReasonDialog"
        onClose={onCloseReason}
        onConfirm={onCancelReasonConfirm}
        titleText={t('Reason_For_Cancellation')}
        body={bodyCancelReason}
        actions={actionsCancelReason}
        size="md"
      />
    </>
  );
};

export default ManagementCloseDialog;
